import { NuggetProfile } from '@backrs_ui/common-ui';

import { Contact } from '@backrs_ui/grow-ui/src/models/models';
import { useLazyGetUserProfileQuery } from '@backrs_ui/grow-ui/src/redux/coachLubav/coachLubav.api';
import { useAppDispatch, useAppSelector } from '@backrs_ui/grow-ui/src/redux/hooks';
import {
  getActiveLubavSession,
  createSessionAction,
} from '@backrs_ui/grow-ui/src/redux/lubavSessionState/lubavSessionState.utils';
import {
  useFetchPublicProfileInfoQuery,
  UserProfileResponse,
} from '@backrs_ui/grow-ui/src/redux/profile/profile';
import {
  setUserInput,
  setUserMessage,
} from '@backrs_ui/grow-ui/src/redux/solutionSession/solutionSession';

import { Box, Theme, SxProps } from '@mui/material';

const getSchoolDisplay = (profile: UserProfileResponse['profile']): string | undefined => {
  const getSchoolValue = (value: string | { id: number; school: string } | undefined) =>
    value ? (typeof value === 'string' ? value : value.school) : undefined;

  const enrollmentLevel = profile.academicEnrollmentTypes?.level;

  if (enrollmentLevel === 'high_school') {
    return getSchoolValue(profile.academicsHighSchoolCurrentEnrollment);
  }

  if (enrollmentLevel === 'post_secondary') {
    return getSchoolValue(profile.academicsPostSecondaryCurrentEnrollment);
  }

  return getSchoolValue(profile.academicsAlmaMaters?.[0]);
};

type UserSelectItemProps = Readonly<{
  contact: Contact;
  onSelect: (userId: string, name: string) => void;
}>;

const UserSelectItem = ({ contact, onSelect }: UserSelectItemProps) => {
  const { data: response, isLoading } = useFetchPublicProfileInfoQuery({ userId: contact.id });

  const profile = response?.profile;
  const { publicProfile, careerOccupation, careerEmployer } = profile || {};

  const displayName =
    [publicProfile?.first_name, publicProfile?.last_name].filter(Boolean).join(' ') || '';
  const location = [publicProfile?.city, publicProfile?.state].filter(Boolean).join(', ');

  return (
    <Box onClick={() => onSelect(contact.id, displayName)} sx={userSelectStyle}>
      <NuggetProfile
        abbreviatedName={displayName}
        location={location}
        jobTitle={careerOccupation}
        employer={Array.isArray(careerEmployer) ? careerEmployer[0] : undefined}
        school={profile ? getSchoolDisplay(profile) || '' : ''}
        role={publicProfile?.role}
        profileImageUrl={publicProfile?.profile_image}
        profileType="INTERNAL"
        contentSize="SMALL"
        onClick={() => onSelect(contact.id, displayName)}
        subText={contact.relevance}
        fitContent={true}
        actionParams={undefined}
        isHovered={false}
        isLoadingSubText={isLoading}
      />
    </Box>
  );
};

type UserSelectProps = Readonly<{
  userSelectionOptions: Contact[] | undefined;
  sendQAMessage: (message: string, LLMInput?: string) => void;
}>;

export const UserSelect = ({ userSelectionOptions, sendQAMessage }: UserSelectProps) => {
  const dispatch = useAppDispatch();
  const activeSession = useAppSelector(getActiveLubavSession);
  const [getUserProfile] = useLazyGetUserProfileQuery();

  if (!activeSession) {
    return null;
  }

  const handleSelect = async (userId: string, name: string) => {
    try {
      const profile = await getUserProfile({
        user_id: userId,
        as_superuser: false,
      }).unwrap();

      if (profile) {
        dispatch(createSessionAction(setUserMessage, activeSession)(name));
        dispatch(
          createSessionAction(setUserInput, activeSession)({ type: 'SINGLE_INPUT', value: userId }),
        );
        sendQAMessage(`ID: ${userId}\n\nProfile: ${profile}`, name);
        dispatch(createSessionAction(setUserMessage, activeSession)(''));
        dispatch(createSessionAction(setUserInput, activeSession)(undefined));
      }
    } catch (err) {
      console.error('Error fetching user profile:', err);
    }
  };

  return (
    <Box sx={userSelectContainerSx}>
      {userSelectionOptions?.map((contact) => (
        <UserSelectItem key={contact.id} contact={contact} onSelect={handleSelect} />
      ))}
    </Box>
  );
};

const userSelectContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-sm'],
});

const userSelectStyle: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  width: '100%',
  alignItems: 'center',
  justifyContent: 'flex-start',
  padding: backrs.spacing['spacing-xxs'],
  borderRadius: backrs.radius['radius-4xl'],
  cursor: 'pointer',
  transition: 'background-color 0.3s ease',
  border: `2px solid transparent`,
  '&:hover': {
    border: `2px solid ${backrs.colors['Brand/500']}`,
  },
  '&:active': {
    backgroundColor: 'rgba(0, 0, 0, 0.2)',
  },
});
